import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import apiService from '../services/api';
import { usePageTitle } from '../hooks/usePageTitle';

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

const InviteDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('inviteDetails');
  usePageTitle(t('title'));

  const { data: invite, isLoading, error } = useQuery({
    queryKey: ['invitation', id],
    queryFn: () => apiService.getInvitationById(id!),
    enabled: !!id,
  });

  if (!id) {
    return (
      <div className="p-4 sm:p-6">
        <p className="text-gray-600 dark:text-gray-400">{t('invalidId')}</p>
        <Link to="/administration/usages" className="text-indigo-600 dark:text-indigo-400 hover:underline mt-2 inline-block">
          {t('backToUsages')}
        </Link>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (error || !invite) {
    return (
      <div className="p-4 sm:p-6">
        <p className="text-red-600 dark:text-red-400">{t('notFound')}</p>
        <Link to="/administration/usages" className="text-indigo-600 dark:text-indigo-400 hover:underline mt-2 inline-block">
          {t('backToUsages')}
        </Link>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-2xl">
      <div className="mb-6 flex items-center gap-4">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          {t('back')}
        </button>
        <Link
          to="/administration/usages"
          className="inline-flex items-center gap-2 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
        >
          {t('backToUsages')}
        </Link>
      </div>

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
          <EnvelopeIcon className="w-5 h-5 text-indigo-500" />
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white">{t('title')}</h1>
        </div>
        <dl className="divide-y divide-gray-200 dark:divide-gray-700">
          <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4">
            <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('from')}</dt>
            <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:col-span-2">{invite.inviter_email || '—'}</dd>
          </div>
          <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4">
            <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('to')}</dt>
            <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:col-span-2">
              {invite.status === 'used' && invite.used_by_user_id ? (
                <Link to={`/users/${invite.used_by_user_id}`} className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium">
                  {invite.invitee_email}
                </Link>
              ) : (
                invite.invitee_email
              )}
            </dd>
          </div>
          <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4">
            <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('sentAt')}</dt>
            <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:col-span-2">{formatDateTime(invite.created_at)}</dd>
          </div>
          {invite.used_at && (
            <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('acceptedAt')}</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:col-span-2">{formatDateTime(invite.used_at)}</dd>
            </div>
          )}
          <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4">
            <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('type')}</dt>
            <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:col-span-2">
              {invite.mode === 'reader' ? t('modeReader') : t('modeFull')}
            </dd>
          </div>
          <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4">
            <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('role')}</dt>
            <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:col-span-2">
              {invite.role === 'admin' ? t('roleAdmin') : t('roleUser')}
            </dd>
          </div>
          <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4">
            <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('status')}</dt>
            <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:col-span-2">{invite.status}</dd>
          </div>
        </dl>
      </div>
    </div>
  );
};

export default InviteDetails;
